import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getDevices, scanDevices, type Device, type ScannedDevice } from '@/api/devices'
import { createRemoteCommand, waitForCommand } from '@/api/remote-commands'
import { getPlanUsage } from '@/api/config'
import { useProxyConfig } from '@/hooks/useProxyConfig'
import { DeviceSheet } from './DeviceSheet'
import { Skeleton } from '@/components/ui/skeleton'
import { toast } from 'sonner'
import { ApiError } from '@/lib/api-error'
import { AlertTriangle, DoorOpen, Monitor, Pencil, Plus, ScanLine, CheckCircle2, Circle } from 'lucide-react'

// ── Status helpers ────────────────────────────────────────────────────────────

type DeviceStatus = 'online' | 'offline' | 'locked' | string

const STATUS_DOT: Record<string, string> = {
  online:  'bg-emerald-500',
  offline: 'bg-slate-400',
  locked:  'bg-amber-500',
}

const STATUS_LABEL: Record<string, string> = {
  online:  'En línea',
  offline: 'Sin conexión',
  locked:  'Bloqueado',
}

function DeviceStatusIndicator({ status }: { status: DeviceStatus }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className={`w-2 h-2 rounded-full shrink-0 ${STATUS_DOT[status] ?? 'bg-slate-400'}`} />
      <span className="text-xs text-text-muted">{STATUS_LABEL[status] ?? status}</span>
    </div>
  )
}

// ── Skeleton card ─────────────────────────────────────────────────────────────

function DeviceCardSkeleton() {
  return (
    <div className="bg-surface border border-border rounded-2xl p-4 flex flex-col gap-4">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3">
          <Skeleton className="w-9 h-9 rounded-xl" />
          <div className="flex flex-col gap-1.5">
            <Skeleton className="h-3.5 w-28 rounded" />
            <Skeleton className="h-2.5 w-16 rounded" />
          </div>
        </div>
        <Skeleton className="w-6 h-6 rounded-lg" />
      </div>
      <Skeleton className="h-9 w-full rounded-xl" />
    </div>
  )
}

// ── Device card ───────────────────────────────────────────────────────────────

interface DeviceCardProps {
  device: Device
  openingDoor: string | null
  onOpenDoor: (id: string) => void
  onEdit: (device: Device) => void
  onNavigate: (id: string) => void
}

function DeviceCard({ device: d, openingDoor, onOpenDoor, onEdit, onNavigate }: DeviceCardProps) {
  return (
    <div className="bg-surface border border-border rounded-2xl p-4 flex flex-col gap-4 hover:border-accent/30 transition-colors">
      <div className="flex items-start justify-between gap-2">
        <button
          onClick={() => onNavigate(d.id)}
          className="flex items-center gap-3 min-w-0 text-left hover:opacity-80 transition-opacity"
        >
          <div className="w-9 h-9 rounded-xl bg-accent/10 flex items-center justify-center shrink-0">
            <Monitor className="w-4 h-4 text-accent" />
          </div>
          <div className="min-w-0">
            <p className="text-text-primary font-semibold text-sm truncate leading-tight">{d.name}</p>
            <DeviceStatusIndicator status={d.status} />
          </div>
        </button>
        <button
          onClick={() => onEdit(d)}
          title="Editar dispositivo"
          aria-label="Editar dispositivo"
          className="p-1.5 rounded-lg text-text-muted hover:text-text-primary hover:bg-border/50 transition-colors shrink-0"
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>
      </div>

      <button
        onClick={() => onOpenDoor(d.id)}
        disabled={!!openingDoor || d.status !== 'online'}
        className={`flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-sm font-medium transition-all ${
          d.status === 'online' && !openingDoor
            ? 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-sm'
            : 'bg-border/40 text-text-muted cursor-not-allowed'
        }`}
        title={d.status === 'locked' ? 'Dispositivo bloqueado por demasiados intentos fallidos' : undefined}
      >
        <DoorOpen className="w-4 h-4" />
        {openingDoor === d.id ? 'Abriendo...' : 'Abrir puerta'}
      </button>
    </div>
  )
}

// ── Scan results ──────────────────────────────────────────────────────────────

interface ScanPanelProps {
  results: ScannedDevice[]
  isAtLimit: boolean
  onClose: () => void
  onAdd: (host: string) => void
  onEdit: (deviceId: string) => void
}

function ScanPanel({ results, isAtLimit, onClose, onAdd, onEdit }: ScanPanelProps) {
  return (
    <div className="border border-border rounded-2xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 bg-surface border-b border-border">
        <div>
          <p className="text-text-primary text-sm font-semibold">Dispositivos encontrados en tu red</p>
          <p className="text-text-muted text-xs">
            {results.length === 0
              ? 'No encontramos dispositivos Hikvision. Verifica que estén encendidos y conectados.'
              : `${results.length} dispositivo${results.length !== 1 ? 's' : ''} Hikvision detectado${results.length !== 1 ? 's' : ''}`
            }
          </p>
        </div>
        <button onClick={onClose} className="text-text-muted text-xs hover:text-text-primary transition-colors px-2 py-1">
          Cerrar
        </button>
      </div>

      {results.length > 0 && (
        <div className="divide-y divide-border">
          {results.map((s) => (
            <div key={s.host} className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="flex items-center gap-3">
                {s.registered
                  ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                  : <Circle className="w-4 h-4 text-text-muted shrink-0" />
                }
                <div>
                  <p className="text-text-primary text-sm font-medium">
                    {s.registered ? s.deviceName : s.host}
                  </p>
                  <p className="text-text-muted text-xs">
                    {s.registered ? `${s.host}:${s.port} · Ya registrado` : `${s.host}:${s.port} · Sin registrar`}
                  </p>
                </div>
              </div>

              {s.registered ? (
                <button
                  onClick={() => onEdit(s.deviceId!)}
                  className="flex items-center gap-1 px-3 py-1.5 border border-border text-text-muted text-xs rounded-lg hover:text-text-primary transition-colors"
                >
                  <Pencil className="w-3 h-3" />
                  Ver
                </button>
              ) : (
                <button
                  onClick={() => !isAtLimit && onAdd(s.host)}
                  disabled={isAtLimit}
                  title={isAtLimit ? 'Límite del plan alcanzado' : undefined}
                  className={`flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg transition-colors font-medium ${
                    isAtLimit
                      ? 'bg-border/40 text-text-muted cursor-not-allowed'
                      : 'bg-accent text-white hover:bg-accent/90'
                  }`}
                >
                  <Plus className="w-3 h-3" />
                  Agregar
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export function DeviceList() {
  const [devices, setDevices] = useState<Device[]>([])
  const [loading, setLoading] = useState(true)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<Device | null>(null)
  const [prefillHost, setPrefillHost] = useState<string | undefined>()
  const [openingDoor, setOpeningDoor] = useState<string | null>(null)
  const [planInfo, setPlanInfo] = useState<{ current: number; limit: number; enforcement: boolean } | null>(null)
  const [scanning, setScanning] = useState(false)
  const [scanResults, setScanResults] = useState<ScannedDevice[] | null>(null)

  const navigate = useNavigate()
  const { proxyUrl, proxyToken, isElectron } = useProxyConfig()

  useEffect(() => {
    Promise.allSettled([getDevices(), getPlanUsage()])
      .then(([devRes, planRes]) => {
        if (devRes.status === 'fulfilled') setDevices(devRes.value.devices)
        else toast.error(devRes.reason instanceof ApiError ? devRes.reason.message : 'Error al cargar dispositivos')
        if (planRes.status === 'fulfilled') {
          const r = planRes.value.resources.find(x => x.key === 'devices')
          if (r) setPlanInfo({ current: r.current, limit: r.limit, enforcement: planRes.value.enforcement })
        }
      })
      .finally(() => setLoading(false))
  }, [])

  async function handleOpenDoor(deviceId: string) {
    if (!deviceId || openingDoor) return
    setOpeningDoor(deviceId)
    try {
      if (isElectron && proxyUrl && proxyToken) {
        const res = await fetch(`${proxyUrl}/door/${deviceId}/unlock`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${proxyToken}` },
        })
        if (!res.ok) throw new Error('Error al abrir puerta')
      } else {
        const { command } = await createRemoteCommand(deviceId, 'OPEN_DOOR')
        const result = await waitForCommand(command.id)
        if (result.status === 'failed') throw new Error(result.error ?? 'Comando fallido')
      }
      toast.success('Puerta abierta')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Error')
    } finally {
      setOpeningDoor(null)
    }
  }

  function openCreate() {
    setEditTarget(null)
    setPrefillHost(undefined)
    setSheetOpen(true)
  }

  function openEdit(device: Device) {
    setEditTarget(device)
    setPrefillHost(undefined)
    setSheetOpen(true)
  }

  function openCreateFromScan(host: string) {
    setEditTarget(null)
    setPrefillHost(host)
    setSheetOpen(true)
  }

  function openEditFromScan(deviceId: string) {
    const d = devices.find((x) => x.id === deviceId)
    if (d) openEdit(d)
  }

  function handleSaved(device: Device) {
    setDevices((prev) => {
      const idx = prev.findIndex((d) => d.id === device.id)
      if (idx >= 0) {
        const next = [...prev]
        next[idx] = device
        return next
      }
      getPlanUsage().then(p => {
        const r = p.resources.find(x => x.key === 'devices')
        if (r) setPlanInfo({ current: r.current, limit: r.limit, enforcement: p.enforcement })
      }).catch(() => {})
      return [...prev, device]
    })
    if (scanResults) {
      setScanResults((prev) =>
        prev!.map((s) => s.host === device.host ? { ...s, registered: true, deviceId: device.id, deviceName: device.name } : s)
      )
    }
  }

  const isAtLimit = Boolean(planInfo && planInfo.enforcement && planInfo.current >= planInfo.limit)

  async function handleScan() {
    if (!proxyUrl || !proxyToken) {
      toast.error('Requiere la app de escritorio conectada')
      return
    }
    setScanning(true)
    setScanResults(null)
    try {
      const result = await scanDevices(proxyUrl, proxyToken)
      setScanResults(result.devices)
      if (result.devices.length === 0) {
        toast.info('No se encontraron dispositivos Hikvision en la red')
      }
    } catch {
      toast.error('Error al escanear la red')
    } finally {
      setScanning(false)
    }
  }

  return (
    <div className="flex flex-col gap-5">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-text-primary font-semibold text-lg">Dispositivos</h1>
          {planInfo && (
            <p className={`text-xs mt-0.5 ${isAtLimit ? 'text-red-500 font-medium' : 'text-text-muted'}`}>
              {planInfo.current} de {planInfo.limit} en tu plan
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isElectron && (
            <button
              onClick={handleScan}
              disabled={scanning}
              className="flex items-center gap-1.5 px-3 py-2 border border-border text-text-muted text-sm rounded-xl hover:text-text-primary hover:bg-border/30 disabled:opacity-50 transition-colors"
            >
              <ScanLine className="w-3.5 h-3.5" />
              {scanning ? 'Buscando...' : 'Buscar en red'}
            </button>
          )}
          <button
            onClick={() => !isAtLimit && openCreate()}
            disabled={isAtLimit}
            title={isAtLimit ? `Límite del plan alcanzado (${planInfo?.current}/${planInfo?.limit})` : undefined}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-xl transition-colors ${
              isAtLimit
                ? 'bg-border/40 text-text-muted cursor-not-allowed'
                : 'bg-accent text-white hover:bg-accent/90 shadow-sm'
            }`}
          >
            {isAtLimit ? <AlertTriangle className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
            Agregar
          </button>
        </div>
      </div>

      {/* Scan results */}
      {scanResults !== null && (
        <ScanPanel
          results={scanResults}
          isAtLimit={isAtLimit}
          onClose={() => setScanResults(null)}
          onAdd={openCreateFromScan}
          onEdit={openEditFromScan}
        />
      )}

      {/* Device grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <DeviceCardSkeleton />
          <DeviceCardSkeleton />
          <DeviceCardSkeleton />
        </div>
      ) : devices.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
          <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center">
            <Monitor className="w-8 h-8 text-accent/60" />
          </div>
          <div>
            <p className="text-text-primary font-semibold">Aún no hay dispositivos</p>
            <p className="text-text-muted text-sm mt-1">Agrega tu primer dispositivo de control de acceso</p>
          </div>
          {!isAtLimit && (
            <button
              onClick={openCreate}
              className="flex items-center gap-2 px-5 py-2.5 bg-accent text-white text-sm font-medium rounded-xl hover:bg-accent/90 transition-colors shadow-sm"
            >
              <Plus className="w-4 h-4" />
              Agregar dispositivo
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {devices.map((d) => (
            <DeviceCard
              key={d.id}
              device={d}
              openingDoor={openingDoor}
              onOpenDoor={handleOpenDoor}
              onEdit={openEdit}
              onNavigate={(id) => navigate(`/devices/${id}`)}
            />
          ))}
        </div>
      )}

      <DeviceSheet
        open={sheetOpen}
        device={editTarget}
        prefillHost={prefillHost}
        onClose={() => setSheetOpen(false)}
        onSaved={handleSaved}
        onDeleted={(id) => setDevices((prev) => prev.filter((d) => d.id !== id))}
      />
    </div>
  )
}
